import React, { useMemo, useState } from "react";
import { Modal } from "@/components/Modal";
import { getGameIcon } from "@/lib/gameIcons";
import { AREAS } from "@/data/mohallaAreas";
import { Search, Heart, Trophy } from "lucide-react";

export const GamesCollectionPanel = ({ open, onClose, games, memories, onSelect, initialTab = "discover" }) => {
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");

  const filtered = useMemo(() => {
    let list = games;
    if (tab === "mine") list = list.filter((g) => memories.includes(g.id));
    if (q.trim()) {
      const s = q.toLowerCase();
      list = list.filter(
        (g) =>
          g.name.toLowerCase().includes(s) ||
          (g.category || "").toLowerCase().includes(s) ||
          (g.alt_names || []).some((a) => a.toLowerCase().includes(s))
      );
    }
    if (area) list = list.filter((g) => g.area === area);
    return list;
  }, [games, memories, tab, q, area]);

  const unlocked = memories.length;
  const total = games.length;
  const complete = total > 0 && unlocked >= total;

  return (
    <Modal open={open} onClose={onClose} testId="games-collection-panel" maxWidth="max-w-4xl">
      <div className="p-5 sm:p-7">
        <div className="flex gap-2">
          <button
            data-testid="games-tab-discover"
            onClick={() => setTab("discover")}
            className={`font-signboard rounded-t-md border-[3px] border-b-0 border-kirana-brown px-4 py-1.5 text-lg font-bold ${tab === "discover" ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown"}`}
          >
            Discover
          </button>
          <button
            data-testid="games-tab-mine"
            onClick={() => setTab("mine")}
            className={`font-signboard flex items-center gap-1.5 rounded-t-md border-[3px] border-b-0 border-kirana-brown px-4 py-1.5 text-lg font-bold ${tab === "mine" ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown"}`}
          >
            <Heart className="h-4 w-4" /> My Childhood Games
          </button>
        </div>

        <div className="rounded-b-md rounded-tr-md border-[3px] border-kirana-brown bg-card p-4">
          {/* progress */}
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between text-sm font-bold text-kirana-brown">
              <span data-testid="games-progress">Games Discovered: {unlocked} / {total}</span>
            </div>
            <div className="h-3 overflow-hidden rounded-full border-2 border-kirana-brown bg-kirana-cream">
              <div className="h-full bg-kirana-red transition-all" style={{ width: `${total ? (unlocked / total) * 100 : 0}%` }} />
            </div>
            {complete && (
              <div className="mt-3 rounded-md border-2 border-kirana-green bg-kirana-green/10 p-3 text-center text-kirana-green">
                <div className="flex items-center justify-center gap-2 font-signboard text-2xl font-bold">
                  <Trophy className="h-6 w-6" /> YOU REMEMBERED THEM ALL.
                </div>
                <p className="mt-1 text-sm italic">
                  From dusty colony roads to rooftop kites, these were the games that needed nothing except a few friends and an empty afternoon.
                </p>
              </div>
            )}
          </div>

          {/* search */}
          <div className="mb-3 flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-1.5">
            <Search className="h-4 w-4 text-kirana-brown/60" />
            <input
              data-testid="games-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a game or local name…"
              className="w-full bg-transparent text-sm text-kirana-brown outline-none placeholder:text-kirana-brown/40"
            />
          </div>

          {/* area filters */}
          <div className="mb-4 flex flex-wrap gap-1.5">
            {AREAS.map((a) => (
              <button
                key={a.id}
                data-testid={`games-filter-area-${a.id}`}
                onClick={() => setArea(area === a.id ? "" : a.id)}
                className={`rounded-full border-2 border-kirana-brown px-3 py-1 text-xs font-bold transition-colors ${area === a.id ? "bg-kirana-red text-white" : "bg-kirana-cream text-kirana-brown hover:bg-kirana-gold"}`}
              >
                {a.name}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="py-10 text-center text-kirana-brown/60">
              {tab === "mine" ? "No games remembered yet. Go walk the colony! 🚶" : "Nothing matches your search."}
            </p>
          ) : (
            <div className="grid max-h-[45vh] grid-cols-2 gap-3 overflow-y-auto scrollbar-thin pr-1 sm:grid-cols-3 lg:grid-cols-4">
              {filtered.map((g) => {
                const Icon = getGameIcon(g.icon);
                const isUnlocked = memories.includes(g.id);
                return (
                  <button
                    key={g.id}
                    data-testid={`games-collection-item-${g.id}`}
                    onClick={() => onSelect(g)}
                    className="group relative flex flex-col items-center gap-1 overflow-hidden rounded-md border-[3px] border-kirana-brown p-3 text-center transition-transform hover:scale-[1.04] hard-shadow-sm"
                    style={{ background: g.placeholder_color }}
                  >
                    <Icon className="h-7 w-7 text-white drop-shadow" />
                    <span className="font-signboard text-xs font-bold leading-tight text-white drop-shadow">{g.name}</span>
                    {isUnlocked && (
                      <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-kirana-brown bg-kirana-cream">
                        <Heart className="h-3 w-3 fill-kirana-red text-kirana-red" />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
